/* eslint-disable react/prop-types */
/**
 * Centre Analytics → Auto-Booking.
 *
 * A frozen dry run of next month's auto-booking, one row per parent, that
 * centre teams review before anyone is charged. The numbers do not move while
 * they are being commented on — that is the whole point of a snapshot, and it
 * is why there is no refresh button here: a re-run takes ~10 minutes over 1,200
 * parents and is driven from the command line when HQ wants a fresh pass.
 *
 * Two comment columns, not a thread: the centre states what it knows about the
 * family, HQ answers, and both stay readable side by side across a long table.
 * The backend stamps who wrote each and rejects an HQ comment from a non-admin.
 */
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  fetchAutoBookingParents,
  fetchAutoBookingRuns,
  fetchAutoBookingSummary,
  updateAutoBookingReview
} from 'api'

import { ParentRow } from './ParentRow'
import * as styles from './styles'

import { showToast } from '../../helpers'

const PAGE_SIZE = 50

const OUTCOMES = [
  'WOULD BOOK',
  'WOULD TERMINATE',
  'ALREADY BOOKED',
  'SKIPPED',
  'ERROR'
]

const REVIEW_STATUSES = [
  { value: 'pending', label: 'Not reviewed' },
  { value: 'no_action', label: 'No action needed' },
  { value: 'centre_flagged', label: 'Centre flagged' },
  { value: 'approved', label: 'Approved to book' },
  { value: 'hold', label: 'On hold' }
]

export const AutoBookingPage = () => {
  const [runs, setRuns] = useState([])
  const [cycle, setCycle] = useState('')
  // A run is identified by (cycle, rail, kind). `kind` separates what WOULD
  // happen from what DID: the commit run sits beside its dry run, not on top.
  const [rail, setRail] = useState('coins')
  const [kind, setKind] = useState('dry_run')
  const [rows, setRows] = useState([])
  const [count, setCount] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [summary, setSummary] = useState([])
  const [filters, setFilters] = useState({
    centre: '',
    outcome: '',
    review_status: '',
    search: ''
  })
  const [toggles, setToggles] = useState({
    will_terminate: false,
    has_shortfall: false,
    no_discount: false,
    charged: false,
    not_charged: false,
    commented: false
  })

  // Months come from the runs that exist. No hardcoded list — a cycle appears
  // on the picker the moment HQ imports its dry run, and October 2026 is the
  // earliest run there will ever be.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const body = await fetchAutoBookingRuns()
        const list = body?.data || []
        if (cancelled) return
        setRuns(list)
        if (list.length) setCycle(list[0].cycle_start)
        else setLoading(false)
      } catch (e) {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const load = useCallback(async () => {
    if (!cycle) return
    setLoading(true)
    try {
      const queries = { cycle, rail, kind, limit: PAGE_SIZE, offset }
      Object.entries(filters).forEach(([k, v]) => {
        if (v) queries[k] = v
      })
      Object.entries(toggles).forEach(([k, v]) => {
        if (v) queries[k] = 'true'
      })
      const body = await fetchAutoBookingParents(queries)
      setRows(body?.data?.results || [])
      setCount(body?.data?.count || 0)
    } catch (e) {
      showToast('Could not load the auto-booking board', 'danger')
    } finally {
      setLoading(false)
    }
  }, [cycle, rail, kind, offset, filters, toggles])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!cycle) return
    let cancelled = false
    ;(async () => {
      try {
        const body = await fetchAutoBookingSummary({ cycle, rail, kind })
        if (!cancelled) setSummary(body?.data || [])
      } catch (e) {
        /* the summary strip is a nicety; a failure must not blank the table */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [cycle, rail, kind])

  // ONE ENTRY PER CYCLE. This mapped over `runs`, and a cycle has up to four of
  // them -- coins and e-mandate, each as a dry run and a commit -- so October
  // 2026 appeared four times in the Month picker, every one selecting the same
  // month. The rail and kind are the VIEW picker's job, below.
  const cycles = useMemo(() => {
    const seen = []
    runs.forEach((r) => {
      if (!seen.includes(r.cycle_start)) seen.push(r.cycle_start)
    })
    return seen
  }, [runs])

  // Only offer the views that actually exist for the chosen month.
  const views = useMemo(
    () =>
      runs
        .filter((r) => r.cycle_start === cycle)
        .map((r) => ({
          key: `${r.rail}|${r.kind}`,
          rail: r.rail,
          kind: r.kind,
          label: `${r.rail === 'coins' ? 'Coins' : 'E-mandate'} · ${
            r.kind === 'commit' ? 'charged' : 'dry run'
          }`,
          count: r.parent_count
        })),
    [runs, cycle]
  )

  const centres = useMemo(
    () =>
      summary
        .map((s) => s.centre_name)
        .filter(Boolean)
        .sort(),
    [summary]
  )

  const activeRun = useMemo(
    () =>
      runs.find(
        (r) => r.cycle_start === cycle && r.rail === rail && r.kind === kind
      ),
    [runs, cycle, rail, kind]
  )

  // When the month changes the previous view may not exist for it; fall back to
  // whatever that month does have rather than showing an empty board.
  useEffect(() => {
    if (!views.length) return
    if (!views.some((v) => v.rail === rail && v.kind === kind)) {
      setRail(views[0].rail)
      setKind(views[0].kind)
      setOffset(0)
    }
  }, [views, rail, kind])

  const onSave = async (uuid, payload) => {
    try {
      const body = await updateAutoBookingReview(uuid, payload)
      const updated = body?.data
      // Patch in place rather than refetching: a reload would jump the reader
      // back to the top of a 1,200-row table mid-review.
      setRows((prev) =>
        prev.map((r) => (r.uuid === uuid ? { ...r, ...updated } : r))
      )
      showToast('Saved', 'success')
    } catch (e) {
      showToast(
        e?.response?.data?.message || 'Could not save that comment',
        'danger'
      )
    }
  }

  const setFilter = (key) => (e) => {
    setOffset(0)
    setFilters((f) => ({ ...f, [key]: e.target.value }))
  }

  const flip = (key) => () => {
    setOffset(0)
    setToggles((t) => ({ ...t, [key]: !t[key] }))
  }

  const totals = useMemo(
    () =>
      summary.reduce(
        (acc, s) => ({
          parents: acc.parents + (s.parents || 0),
          pending: acc.pending + (s.pending || 0),
          approved: acc.approved + (s.approved || 0),
          flagged: acc.flagged + (s.flagged || 0),
          terminating: acc.terminating + (s.terminating || 0)
        }),
        { parents: 0, pending: 0, approved: 0, flagged: 0, terminating: 0 }
      ),
    [summary]
  )

  if (!runs.length && !loading) {
    return (
      <styles.Empty>
        <Typography variant="h6">
          No auto-booking run has been imported yet.
        </Typography>
        <Typography variant="body2">
          HQ imports a dry run with{' '}
          <code>manage.py import_autobooking_dry_run</code>; it appears here as
          soon as it lands.
        </Typography>
      </styles.Empty>
    )
  }

  return (
    <styles.Wrapper>
      <styles.Header>
        <Box display="flex" alignItems="center" style={{ gap: 16 }}>
          <FormControl
            variant="outlined"
            size="small"
            style={{ minWidth: 170 }}
          >
            <InputLabel>Month</InputLabel>
            <Select
              label="Month"
              value={cycle}
              onChange={(e) => {
                setOffset(0)
                setCycle(e.target.value)
              }}
            >
              {cycles.map((c) => (
                <MenuItem key={c} value={c}>
                  {dayjs(c).format('MMMM YYYY')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* SHOWN EVEN FOR A SINGLE VIEW (owner, 2026-10-25). It used to appear
              only when a month had more than one, so a cycle with just the
              e-mandate dry run gave the reader nothing naming what they were
              looking at -- and the board carries two rails whose numbers mean
              very different things. */}
          {views.length ? (
            <FormControl
              variant="outlined"
              size="small"
              style={{ minWidth: 200 }}
            >
              <InputLabel>View</InputLabel>
              <Select
                label="View"
                value={`${rail}|${kind}`}
                onChange={(e) => {
                  const [r, k] = e.target.value.split('|')
                  setOffset(0)
                  setRail(r)
                  setKind(k)
                }}
              >
                {views.map((v) => (
                  <MenuItem key={v.key} value={v.key}>
                    {v.label} ({v.count})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          {activeRun ? (
            <Tooltip
              title={
                'These figures were frozen when the run was taken. A class paused since then still reads as it did at run time.'
              }
            >
              <styles.AsOf>
                snapshot taken {dayjs(activeRun.created).format('D MMM, HH:mm')}
                {activeRun.generated_by_name
                  ? ` by ${activeRun.generated_by_name}`
                  : ''}
              </styles.AsOf>
            </Tooltip>
          ) : null}
        </Box>

        {summary.length ? (
          <styles.Totals>
            <span>{totals.parents} parents</span>
            <span>{totals.pending} pending</span>
            <span>{totals.flagged} flagged</span>
            <span>{totals.approved} approved</span>
            {totals.terminating ? (
              <styles.Danger>
                {totals.terminating} will be terminated
              </styles.Danger>
            ) : null}
          </styles.Totals>
        ) : null}
      </styles.Header>

      <styles.Filters>
        <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
          <InputLabel>Centre</InputLabel>
          <Select
            label="Centre"
            value={filters.centre}
            onChange={setFilter('centre')}
          >
            <MenuItem value="">All centres</MenuItem>
            {centres.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" style={{ minWidth: 170 }}>
          <InputLabel>Outcome</InputLabel>
          <Select
            label="Outcome"
            value={filters.outcome}
            onChange={setFilter('outcome')}
          >
            <MenuItem value="">All outcomes</MenuItem>
            {OUTCOMES.map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" style={{ minWidth: 170 }}>
          <InputLabel>Review</InputLabel>
          <Select
            label="Review"
            value={filters.review_status}
            onChange={setFilter('review_status')}
          >
            <MenuItem value="">Any status</MenuItem>
            {REVIEW_STATUSES.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          variant="outlined"
          size="small"
          label="Parent, phone or child"
          value={filters.search}
          onChange={setFilter('search')}
          style={{ minWidth: 220 }}
        />

        <Chip
          label="Being terminated"
          clickable
          color={toggles.will_terminate ? 'secondary' : 'default'}
          onClick={flip('will_terminate')}
        />
        <Chip
          label="Short on coins"
          clickable
          color={toggles.has_shortfall ? 'secondary' : 'default'}
          onClick={flip('has_shortfall')}
        />
        <Chip
          label="No discount"
          clickable
          color={toggles.no_discount ? 'secondary' : 'default'}
          onClick={flip('no_discount')}
        />
        {kind === 'commit' ? (
          <>
            <Chip
              label="Debit triggered"
              clickable
              color={toggles.charged ? 'secondary' : 'default'}
              onClick={flip('charged')}
            />
            <Chip
              label="Not charged"
              clickable
              color={toggles.not_charged ? 'secondary' : 'default'}
              onClick={flip('not_charged')}
            />
          </>
        ) : null}
        <Chip
          label="Has a centre comment"
          clickable
          color={toggles.commented ? 'secondary' : 'default'}
          onClick={flip('commented')}
        />
      </styles.Filters>

      {loading ? (
        <styles.Loading>
          <CircularProgress size={28} />
        </styles.Loading>
      ) : (
        <>
          <styles.TableHead>
            <span>Parent</span>
            <span>Children &amp; categories</span>
            <span>Outcome</span>
            <span>Money</span>
            <span>Centre comment</span>
            <span>HQ comment</span>
          </styles.TableHead>

          {rows.map((row) => (
            <ParentRow
              key={row.uuid}
              row={row}
              onSave={onSave}
              isCommit={kind === 'commit'}
            />
          ))}

          {!rows.length ? (
            <styles.Empty>
              <Typography variant="body2">
                No parents match these filters.
              </Typography>
            </styles.Empty>
          ) : null}

          <styles.Pager>
            <Button
              size="small"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </Button>
            <span>
              {count ? offset + 1 : 0}–{Math.min(offset + PAGE_SIZE, count)} of{' '}
              {count}
            </span>
            <Button
              size="small"
              disabled={offset + PAGE_SIZE >= count}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </Button>
          </styles.Pager>
        </>
      )}
    </styles.Wrapper>
  )
}

export default AutoBookingPage
